import type {
  FormulaDiagnostic,
  FormulaScopeWarning,
} from '@/api/customFields/formulaFields.interface';

// Shown where a formula runs but produces nothing to store.
export const NO_RESULT = '-';

const FALLBACK = 'This formula cannot run';

// The bounded set the formula language reports, plus the codes the rule's own
// validation adds. One line each, restating the refusal: the editor already
// underlines where it happened.
const DIAGNOSTIC_MESSAGES: Record<string, string> = {
  err_formula_syntax: 'This formula is not written correctly',
  err_formula_unknown_field: 'This formula names a field that does not exist',
  err_formula_dangling_reference:
    'This formula reads a field that has been removed',
  err_formula_self_reference: 'A field cannot calculate itself',
  err_formula_circular_reference:
    'These fields calculate each other, so neither can be worked out',
  err_formula_chain_too_deep: 'This formula reads through too many fields',
  err_formula_too_long: 'This formula is too long',
  err_formula_too_many_references: 'This formula reads too many fields',
  err_formula_too_deeply_nested: 'This formula is nested too deeply',
  err_formula_unknown_function:
    'This formula uses a function that does not exist',
  err_formula_unknown_operator:
    'This formula uses an operator that does not exist',
  err_formula_division_by_zero: 'This formula divides by zero',
  err_formula_not_computable: 'This formula cannot be worked out',
  err_formula_result_type: 'This formula gives a value this field cannot hold',
  err_formula_unsupported_construct:
    'This formula uses something we cannot calculate',
  // A dropdown's options are created from the formula's own literals, so a
  // result that is built rather than written — upper({role}), a concat, a
  // field on its own — creates no options and would resolve to nothing on
  // every offer. Wording from the backend, so both sides say the same thing.
  err_formula_select_result_not_literal:
    'A dropdown\u2019s formula has to answer with the option names themselves, like if(\u2026, "Senior", "Mid")',
  // No edit to the formula can resolve this one — two fields answer to the
  // same token, so a field has to be renamed.
  err_formula_fields_ambiguous:
    'Two fields share this name, so rename one of them before using it here',
  err_formula_field_inaccessible: 'This formula reads a field you cannot see',
  err_formula_target_multi:
    'A field that holds several values cannot be calculated',
  err_formula_target_not_computable: 'This field type cannot be calculated',
  err_formula_fields_disabled: 'Formula fields are turned off for this account',
  err_formula_result_unstorable:
    'This formula can answer with a value this field cannot hold',
  err_formula_date_format_invalid:
    'This date format has something in it we cannot fill in',
  err_formula_invalid_unit: 'This function does not know that unit',
};

// A list read as prose: "week, weeks or month".
const asList = (items: string[]) =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;

const quotedList = (items: unknown) =>
  Array.isArray(items)
    ? asList(items.map((item) => `\u201c${String(item)}\u201d`))
    : '';

/**
 * A date format carrying something the formatter will not substitute, e.g.
 * "DD/MM/YYYY" — only `dd` and `yyyy` are filled in, so the rest is printed
 * into the letter as written. Naming the offending runs is the whole value of
 * the message, and there is no position to underline (core#6190).
 *
 * Deliberately says nothing about the format being correct otherwise: the
 * check catches mistyped tokens only, and token letters inside ordinary words
 * are substituted before it runs.
 */
const describeDateFormat = (detail?: Record<string, unknown>) => {
  const unknown = quotedList(detail?.unknown);
  if (!unknown) return DIAGNOSTIC_MESSAGES.err_formula_date_format_invalid;
  const many = Array.isArray(detail?.unknown) && detail.unknown.length > 1;
  // Named, because one of these arrives per offending call: two format_date
  // calls give two refusals, and without the format they read identically
  // (core#6190, revised).
  const format = detail?.format ? ` \u201c${String(detail.format)}\u201d` : '';
  return `The date format${format} cannot fill in ${unknown}, so ${
    many ? 'they are' : 'it is'
  } printed as written`;
};

/**
 * A unit the function does not take. The accepted list comes off the payload,
 * never from here: the same code is raised for dateadd and datediff with a
 * wider set that includes days, where start_day answers nothing (core#6190).
 */
const describeInvalidUnit = (detail?: Record<string, unknown>) => {
  const unit = detail?.unit === undefined ? '' : String(detail.unit);
  const fn = detail?.function === undefined ? '' : String(detail.function);
  const expects = quotedList(detail?.expects);
  if (!unit || !expects) return DIAGNOSTIC_MESSAGES.err_formula_invalid_unit;
  const subject = fn ? `\u201c${fn}\u201d` : 'This function';
  return `${subject} does not take \u201c${unit}\u201d \u2014 it takes ${expects}`;
};

// What each kind will actually accept, for the literal it refused. The server
// type-checked the formula but not the value it answers with: `url`,
// `enum_boolean` and `string` all type as text, so `if(c, "YES", "NO")` on a
// Yes/No field read as valid and then broke the offer save.
const describeUnstorable = (detail?: Record<string, unknown>) => {
  const value = detail?.value === undefined ? '' : String(detail.value);
  const quoted = value ? `“${value}”` : 'That answer';

  switch (String(detail?.kind ?? '')) {
    case 'enum_boolean':
      return `A Yes/No field can only hold Y or N, and ${quoted} is not one of them`;
    case 'url':
      return `${quoted} is not a valid link`;
    case 'string':
      return `${quoted} is longer than 255 characters`;
    default:
      return DIAGNOSTIC_MESSAGES.err_formula_result_unstorable;
  }
};

// The three kinds the language reasons about, in the words a person uses.
const TYPE_WORDS: Record<string, string> = {
  numeric: 'a number',
  string: 'text',
  logical: 'yes or no',
};

/**
 * "This formula gives a value this field cannot hold" is true and unhelpful:
 * it never says what the field holds or what the formula works out. The server
 * sends both, so the message names them.
 */
const describeResultType = (detail?: Record<string, unknown>) => {
  const wanted = TYPE_WORDS[String(detail?.expected ?? '')];
  const got = TYPE_WORDS[String(detail?.actual ?? '')];
  if (!wanted) return DIAGNOSTIC_MESSAGES.err_formula_result_type;
  // `conflicting` means the branches disagree with each other, so there is no
  // single "got" to name — say which branch is the odd one out instead.
  if (!got) {
    return `This field holds ${wanted}, and the formula does not always work one out`;
  }
  return `This field holds ${wanted}, but the formula works out ${got}`;
};

// An unmapped code is still a real failure, so it reads as one rather than
// showing the recruiter a raw `err_` string.
export const describeDiagnostic = (diagnostic?: FormulaDiagnostic) => {
  if (diagnostic?.code === 'err_formula_result_type') {
    return describeResultType(diagnostic.detail);
  }
  // One of these arrives per offending literal, so `if(c, "YES", "NO")` on a
  // Yes/No field reports both rather than only the first.
  if (diagnostic?.code === 'err_formula_result_unstorable') {
    return describeUnstorable(diagnostic.detail);
  }
  if (diagnostic?.code === 'err_formula_date_format_invalid') {
    return describeDateFormat(diagnostic.detail);
  }
  if (diagnostic?.code === 'err_formula_invalid_unit') {
    return describeInvalidUnit(diagnostic.detail);
  }
  return (diagnostic?.code && DIAGNOSTIC_MESSAGES[diagnostic.code]) || FALLBACK;
};

/**
 * A field the formula reads that this one's scope does not reach. Not a
 * refusal: the rule saves, and on the offers where the input is missing the
 * value simply cannot be worked out — which is what the admin needs telling
 * before a recruiter meets it.
 *
 * Narrower by both office and department reads as one sentence, not two.
 */
export const describeScopeWarning = ({
  label,
  reasons = [],
}: FormulaScopeWarning): string => {
  if (reasons.includes('err_formula_field_unscoped'))
    return `"${label}" is not on any offer form, so this value can never be worked out`;

  const narrower = [
    reasons.includes('err_formula_field_office_narrower') && 'office',
    reasons.includes('err_formula_field_department_narrower') && 'department',
  ].filter(Boolean);

  if (narrower.length === 0)
    return `"${label}" is not on every offer this field shows on, so this value cannot always be worked out`;

  return `"${label}" is on fewer ${narrower.join('s and ')}s than this field, so this value cannot be worked out on the rest`;
};
